/* eslint-disable @typescript-eslint/no-unused-vars */

/**
 * универсальный хук для блокировки/разблокировки скролла страницы.
 * особенности:
 * - реф-счётчик: несколько компонентов могут одновременно держать блокировку.
 * - без лишних перерисовок, только изменение inline-стилей body.
 * - сохраняет и восстанавливает позицию скролла.
 * - компенсирует исчезновение вертикального скроллбара (чтобы не дёргалась вёрстка на десктопе).
 * - SSR-safe: аккуратно проверяет наличие window/document.
 */

import { useEffect, useRef } from 'react';

type Options = {
    reserveScrollBarGap?: boolean; // добавлять ли компенсацию исчезнувшего скроллбара (padding-right, по умолчанию true).
};

let lockCount = 0;
let applied = false;
let savedScrollY = 0;

const prevInlineStyles: Partial<Pick<CSSStyleDeclaration, 'position' | 'top' | 'left' | 'right' | 'width' | 'overflow' | 'paddingRight'>> = {};

function getScrollbarWidth(): number {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

function lockBody(options: Required<Options>) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (applied) {
        lockCount += 1;
        return;
    }
    const body = document.body;
    const docEl = document.documentElement;
    savedScrollY = window.scrollY || docEl.scrollTop || body.scrollTop || 0;
    prevInlineStyles.position = body.style.position;
    prevInlineStyles.top = body.style.top;
    prevInlineStyles.left = body.style.left;
    prevInlineStyles.right = body.style.right;
    prevInlineStyles.width = body.style.width;
    prevInlineStyles.overflow = body.style.overflow;
    prevInlineStyles.paddingRight = body.style.paddingRight;
    if (options.reserveScrollBarGap) {
        const scrollbarWidth = getScrollbarWidth();
        if (scrollbarWidth > 0) {
            const computed = window.getComputedStyle(body);
            const currentPaddingRight = parseInt(computed.paddingRight || '0', 10) || 0;
            body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
        }
    }
    body.style.position = 'fixed';
    body.style.top = `-${savedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    applied = true;
    lockCount = 1;
}

function unlockBody() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!applied) return;
    if (lockCount > 1) {
        lockCount -= 1;
        return;
    }
    const body = document.body;
    body.style.position = prevInlineStyles.position ?? '';
    body.style.top = prevInlineStyles.top ?? '';
    body.style.left = prevInlineStyles.left ?? '';
    body.style.right = prevInlineStyles.right ?? '';
    body.style.width = prevInlineStyles.width ?? '';
    body.style.overflow = prevInlineStyles.overflow ?? '';
    body.style.paddingRight = prevInlineStyles.paddingRight ?? '';
    window.scrollTo(0, savedScrollY);
    applied = false;
    lockCount = 0;
}

export function useBodyScrollLock(isLocked: boolean, options: Options = {}): void {
    const optsRef = useRef<Required<Options>>({
        reserveScrollBarGap: options.reserveScrollBarGap ?? true,
    });
    useEffect(() => {
        if (isLocked) {
            lockBody(optsRef.current);
            return () => {
                unlockBody();
            };
        }
        unlockBody();
        return () => {
            unlockBody();
        };
    }, [isLocked]);
}

export default useBodyScrollLock;
